import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Statistic, Spin, Select, Space, DatePicker, Tabs, Alert, Drawer, Table, Tag } from 'antd';
import { DollarOutlined, ThunderboltOutlined, CalendarOutlined, TeamOutlined } from '@ant-design/icons';
import {
  ComposedChart, Scatter, Bar, Line, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

const REGIONS = ['NSW1', 'QLD1', 'SA1', 'TAS1', 'VIC1'];

const fmt$ = (v) => v == null ? '-' : `$${Number(v).toFixed(2)}`;
const fmtMW = (v) => v == null ? '-' : `${Number(v).toFixed(1)} MW`;

// Stable colour assignment per DUID — hash → HSL hue
function duidColor(duid) {
  if (!duid) return '#999';
  let h = 0;
  for (let i = 0; i < duid.length; i++) h = (h * 31 + duid.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue}, 65%, 50%)`;
}

const periodTimeTick = (p) => {
  const totalMin = (p - 1) * 5;
  const h = Math.floor(totalMin / 60);
  return `${String(h).padStart(2, '0')}:00`;
};

function MarginalMarketMakerTab() {
  const [date, setDate] = useState(dayjs().subtract(2, 'day'));
  const [region, setRegion] = useState('SA1');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerPid, setDrawerPid] = useState(null);

  useEffect(() => {
    if (!date || !region) return;
    setLoading(true);
    setError(null);
    api.get('/api/aemo-energy-market/marginal', {
      params: { date: date.format('YYYY-MM-DD'), region },
    })
      .then(r => setData(r.data.data))
      .catch(e => setError(e.response?.data?.message || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [date, region]);

  const intervals = data?.intervals || [];

  // KPIs
  const avgRrp = intervals.length > 0
    ? intervals.reduce((s, i) => s + i.rrp, 0) / intervals.length
    : null;
  const uniqMarginals = new Set(intervals.map(i => i.marginalDuid).filter(Boolean));
  const topMarginal = useMemo(() => {
    const counts = {};
    for (const i of intervals) {
      if (!i.marginalDuid) continue;
      counts[i.marginalDuid] = (counts[i.marginalDuid] || 0) + 1;
    }
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return entries[0] || null;
  }, [intervals]);

  // Chart 1 data: scatter point = marginalBandPrice (what we found); line = rrp (settlement truth)
  const chart1Data = intervals
    .filter(i => i.marginalDuid != null)
    .map(i => ({
      periodId: i.periodId,
      label: i.label,
      rrp: i.rrp,
      marginalBandPrice: i.marginalBandPrice,
      duid: i.marginalDuid,
      participantId: i.marginalParticipant,
      bandIndex: i.marginalBandIndex,
      bandMw: i.marginalBandMw,
      totalDispatchMw: i.totalDispatchMw,
    }));

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <DatePicker
            value={date}
            onChange={(d) => setDate(d)}
            format="DD MMM YYYY"
            size="small"
            allowClear={false}
          />
          <Select
            value={region}
            onChange={setRegion}
            style={{ width: 110 }}
            size="small"
            options={REGIONS.map(r => ({ label: r, value: r }))}
          />
          <span style={{ fontSize: 11, color: '#999' }}>AEST · 5-min dispatch intervals</span>
        </Space>
      </Card>

      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setError(null)} />}

      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card style={{ minHeight: 100 }}>
              <Statistic title="Generators in region" value={data?.generatorCount || 0}
                prefix={<TeamOutlined style={{ color: '#54A2CC' }} />} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card style={{ minHeight: 100 }}>
              <Statistic title="Avg RRP ($/MWh)" value={avgRrp != null ? avgRrp.toFixed(2) : '-'}
                prefix={<DollarOutlined style={{ color: '#05A677' }} />} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card style={{ minHeight: 100 }}>
              <Statistic title="Unique price-makers" value={uniqMarginals.size}
                prefix={<ThunderboltOutlined style={{ color: '#f5b759' }} />} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card style={{ minHeight: 100 }}>
              <Statistic
                title="Most-frequent marginal DUID"
                value={topMarginal ? topMarginal[0] : '-'}
                valueStyle={{ fontSize: 18 }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {topMarginal ? `${topMarginal[1]} / ${intervals.length} intervals` : '-'}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Chart 1: marginal-band price (scatter, coloured by DUID) vs RRP (line) */}
        <Card
          style={{ marginTop: 16 }}
          title="Marginal-band price (dots) vs settlement RRP (line)"
          extra={<span style={{ fontSize: 12, color: '#999' }}>dots should sit on the line if the marginal DUID was identified correctly</span>}
        >
          <ResponsiveContainer width="100%" height={360}>
            <ComposedChart data={chart1Data} margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number" dataKey="periodId"
                domain={[1, 288]} ticks={[1, 48, 96, 144, 192, 240, 288]}
                tickFormatter={periodTimeTick}
                label={{ value: 'Time of day (AEST)', position: 'insideBottom', offset: -10, fontSize: 12 }}
                fontSize={11}
              />
              <YAxis
                tickFormatter={(v) => `$${Math.round(v)}`}
                label={{ value: '$/MWh', angle: -90, position: 'insideLeft', fontSize: 12 }}
                fontSize={11}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', padding: 8, fontSize: 12 }}>
                      <div style={{ fontWeight: 600 }}>{p.label} (period {p.periodId})</div>
                      <div>Settlement RRP: <b>{fmt$(p.rrp)}/MWh</b></div>
                      <div>Marginal band price: <b style={{ color: duidColor(p.duid) }}>{fmt$(p.marginalBandPrice)}/MWh</b></div>
                      <div>Marginal DUID: <b style={{ color: duidColor(p.duid) }}>{p.duid}</b></div>
                      <div>Participant: {p.participantId || '-'}</div>
                      <div>Band #{p.bandIndex} · Bid cap: {fmtMW(p.bandMw)}</div>
                      <div>Region dispatch: {fmtMW(p.totalDispatchMw)}</div>
                    </div>
                  );
                }}
              />
              <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 8 }} />
              <Line
                type="monotone" dataKey="rrp" name="Settlement RRP"
                stroke="#262B40" strokeWidth={2} dot={false} isAnimationActive={false}
              />
              <Scatter dataKey="marginalBandPrice" name="Marginal-band price" isAnimationActive={false}>
                {chart1Data.map((d, i) => (
                  <Cell key={i} fill={duidColor(d.duid)} />
                ))}
              </Scatter>
            </ComposedChart>
          </ResponsiveContainer>
        </Card>

        {/* Chart 2: per-interval bid stack (288 bars) + demand line */}
        <Card
          style={{ marginTop: 16 }}
          title="Bid-stack volume per interval — coloured slice is the marginal DUID's band"
          extra={<span style={{ fontSize: 12, color: '#999' }}>red line = region dispatch (demand)</span>}
        >
          <ResponsiveContainer width="100%" height={420}>
            <ComposedChart data={intervals} margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number" dataKey="periodId"
                domain={[1, 288]} ticks={[1, 48, 96, 144, 192, 240, 288]}
                tickFormatter={periodTimeTick}
                label={{ value: 'Time of day (AEST)', position: 'insideBottom', offset: -10, fontSize: 12 }}
                fontSize={11}
              />
              <YAxis
                tickFormatter={(v) => `${Math.round(v).toLocaleString()}`}
                label={{ value: 'MW', angle: -90, position: 'insideLeft', fontSize: 12 }}
                fontSize={11}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', padding: 8, fontSize: 12 }}>
                      <div style={{ fontWeight: 600 }}>{p.label} (period {p.periodId})</div>
                      <div>Dispatch (demand): <b>{fmtMW(p.totalDispatchMw)}</b></div>
                      <div>Marginal DUID: <b style={{ color: duidColor(p.marginalDuid) }}>{p.marginalDuid}</b></div>
                      <div>Marginal band: #{p.marginalBandIndex} · price <b>{fmt$(p.marginalBandPrice)}</b> · cap {fmtMW(p.marginalMw)}</div>
                      <div style={{ marginTop: 4, color: '#666' }}>
                        ≤ marginal: {fmtMW(p.belowMw)} · marginal: {fmtMW(p.marginalMw)} · &gt; marginal: {fmtMW(p.aboveMw)}
                      </div>
                    </div>
                  );
                }}
              />
              <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 8 }} />
              <Bar
                dataKey="belowMw" stackId="stack" name="≤ marginal price (dispatched)"
                fill="#bdbdbd" isAnimationActive={false} cursor="pointer"
                onClick={(d) => setDrawerPid(d?.periodId)}
              />
              <Bar
                dataKey="marginalMw" stackId="stack" name="marginal DUID's band"
                isAnimationActive={false} cursor="pointer"
                onClick={(d) => setDrawerPid(d?.periodId)}
              >
                {intervals.map((d, i) => (
                  <Cell key={i} fill={duidColor(d.marginalDuid)} />
                ))}
              </Bar>
              <Bar
                dataKey="aboveMw" stackId="stack" name="&gt; marginal price (idle)"
                fill="#e5e5e5" isAnimationActive={false} cursor="pointer"
                onClick={(d) => setDrawerPid(d?.periodId)}
              />
              <Line
                type="monotone" dataKey="totalDispatchMw" name="Region dispatch"
                stroke="#FA5252" strokeWidth={2} dot={false} isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
            Click any bar to open a drawer listing every DUID's band for that interval (cheapest first).
          </div>
        </Card>

        <BidStackDrawer
          periodId={drawerPid}
          interval={drawerPid ? intervals.find(i => i.periodId === drawerPid) : null}
          stack={drawerPid ? (data?.stackByPeriod?.[drawerPid] || []) : []}
          onClose={() => setDrawerPid(null)}
        />
      </Spin>
    </>
  );
}

function BidStackDrawer({ periodId, interval, stack, onClose }) {
  const open = periodId != null;
  const totalSupply = stack.length > 0 ? stack[stack.length - 1].cumulativeMw : 0;
  const demand = interval?.totalDispatchMw ?? 0;
  const rrp = interval?.rrp;
  const marginalDuid = interval?.marginalDuid;

  const columns = [
    {
      title: '#', key: 'idx', width: 50, align: 'right',
      render: (_, __, i) => i + 1,
    },
    {
      title: 'Price ($/MWh)', dataIndex: 'price', width: 120, align: 'right',
      render: (v, row) => (
        <span style={{ fontWeight: row.isMarginal ? 700 : 400, color: row.isMarginal ? '#FA5252' : undefined, fontVariantNumeric: 'tabular-nums' }}>
          {fmt$(v)}{row.isMarginal ? ' ⭐' : ''}
        </span>
      ),
    },
    {
      title: 'DUID', dataIndex: 'duid', width: 110,
      render: (v) => <span style={{ color: duidColor(v), fontWeight: 600 }}>{v}</span>,
    },
    { title: 'Participant', dataIndex: 'participantId', width: 110 },
    {
      title: 'Band #', dataIndex: 'bandIndex', width: 70, align: 'right',
      render: (v) => `#${v}`,
    },
    {
      title: 'MW', dataIndex: 'mw', width: 90, align: 'right',
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v.toFixed(2)}</span>,
    },
    {
      title: 'Cumulative MW', dataIndex: 'cumulativeMw', width: 130, align: 'right',
      render: (v, row) => {
        const passedDemand = v >= demand;
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums', color: passedDemand ? '#999' : '#05A677', fontWeight: row.isMarginal ? 700 : 400 }}>
            {v.toFixed(1)}
          </span>
        );
      },
    },
  ];

  return (
    <Drawer
      title={
        interval ? (
          <Space size="middle" wrap>
            <span style={{ fontWeight: 700 }}>{interval.label} (period {periodId})</span>
            <Tag color="blue">RRP {fmt$(rrp)}</Tag>
            <Tag color="red">Dispatch {fmtMW(demand)}</Tag>
            <Tag>Total bid (capped) {fmtMW(totalSupply)}</Tag>
            {marginalDuid && (
              <Tag style={{ background: duidColor(marginalDuid), color: '#fff', borderColor: 'transparent' }}>
                ⭐ Marginal: {marginalDuid} band #{interval.marginalBandIndex} @ {fmt$(interval.marginalBandPrice)}
              </Tag>
            )}
          </Space>
        ) : null
      }
      placement="right"
      width={760}
      onClose={onClose}
      open={open}
      destroyOnClose
    >
      {stack.length === 0 ? (
        <div style={{ color: '#999' }}>No stack data for this interval.</div>
      ) : (
        <Table
          dataSource={stack.map((s, i) => ({ ...s, _k: `${s.duid}-${s.bandIndex}-${i}` }))}
          columns={columns}
          rowKey="_k"
          pagination={false}
          size="small"
          scroll={{ y: 'calc(100vh - 200px)' }}
          rowClassName={(row) => row.isMarginal ? 'row-marginal' : ''}
        />
      )}
      <style>{`
        .row-marginal td { background: #fff7e6 !important; }
      `}</style>
    </Drawer>
  );
}

export default function AemoEnergyMarket() {
  return (
    <PageLayout>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1f2937' }}>AEMO Energy Market</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
          Wholesale spot price + bidding behaviour (NEM 5-min dispatch)
        </div>
      </div>
      <Tabs
        defaultActiveKey="marginal"
        items={[
          { key: 'marginal', label: 'Marginal Market Maker', children: <MarginalMarketMakerTab /> },
        ]}
      />
    </PageLayout>
  );
}
